import { Operator } from 'unleash-client/lib/strategy/strategy';
import { PayloadType } from 'unleash-client/lib/variant';
import { z } from 'zod/v4';

const nullToUndefined = <T>(val: T | null): T | undefined => val ?? undefined;
const nullToArray = <T>(val: T[] | null): T[] => val ?? [];
const nullToRecord = (val: Record<string, unknown> | null): Record<string, unknown> => val ?? {};

const UnleashPayloadSchema = z.object({
  type: z.enum(PayloadType),
  value: z.string(),
});

const UnleashOverrideSchema = z.object({
  contextName: z.string(),
  values: z.string().array(),
});

const UnleashVariantDefinitionSchema = z.object({
  name: z.string(),
  weight: z.number(),
  stickiness: z.string().optional(),
  payload: UnleashPayloadSchema.optional(),
  overrides: UnleashOverrideSchema.array().nullable().transform(nullToUndefined).optional(),
});

const UnleashConstraintSchema = z.object({
  contextName: z.string(),
  operator: z.enum(Operator),
  inverted: z
    .boolean()
    .nullable()
    .optional()
    .transform((v) => v ?? false),
  values: z
    .string()
    .array()
    .nullable()
    .optional()
    .transform((v) => v ?? []),
  value: z.union([z.string(), z.number()]).nullable().transform(nullToUndefined).optional(),
  caseInsensitive: z.boolean().nullable().transform(nullToUndefined).optional(),
});

const UnleashStrategySchema = z.object({
  name: z.string(),
  parameters: z.record(z.string(), z.unknown()).nullable().transform(nullToRecord),
  constraints: UnleashConstraintSchema.array().nullable().transform(nullToArray),
  segments: z.number().array().nullable().transform(nullToUndefined).optional(),
  variants: UnleashVariantDefinitionSchema.array().nullable().transform(nullToUndefined).optional(),
});

const UnleashDependencySchema = z.object({
  feature: z.string(),
  variants: z.string().array().optional(),
  enabled: z.boolean().optional(),
});

const UnleashToggleSchema = z.object({
  name: z.string(),
  type: z.string().optional(),
  project: z.string().optional(),
  description: z.string().nullable().transform(nullToUndefined).optional(),
  enabled: z.boolean(),
  stale: z.boolean().nullable().transform(nullToUndefined).optional(),
  impressionData: z.boolean().nullable().transform(nullToUndefined).optional(),
  strategies: UnleashStrategySchema.array().nullable().transform(nullToUndefined).optional(),
  variants: UnleashVariantDefinitionSchema.array().nullable().transform(nullToUndefined).optional(),
  dependencies: UnleashDependencySchema.array().nullable().transform(nullToUndefined).optional(),
});

export const UnleashTogglesSchema = z.array(UnleashToggleSchema);

export type UnleashToggle = z.infer<typeof UnleashToggleSchema>;
export type UnleashStrategy = z.infer<typeof UnleashStrategySchema>;
export type UnleashConstraint = z.infer<typeof UnleashConstraintSchema>;
export type UnleashVariantDefinition = z.infer<typeof UnleashVariantDefinitionSchema>;
